import { supabase } from '../../shared/supabase/supabaseClient';
import { captureOpsFailure } from '../../shared/sentry/sentryOps';
import type {
  WorkOrder,
  WorkOrderDetail,
  WorkOrderDraft,
  WorkOrderListItem,
  WorkOrderStatus,
  WorkOrderTask,
  WorkOrderPart,
  WorkOrderPhoto,
  WorkOrderChecklist,
  WorkOrderChecklistItem,
  WorkOrderPaymentKind,
  WorkOrderPaymentStatus,
} from '../domain/types';
import { normalizeWorkOrderSubjectType } from '../domain/workOrderSubject';

type WorkOrderRow = {
  id: string;
  organization_id: string;
  branch_id: string;
  subject_type: WorkOrderDraft['subjectType'];
  subject_id: string | null;
  customer_id: string | null;
  customer_name: string;
  status: WorkOrderStatus;
  notes: string | null;
  appointment_id: string | null;
  sale_id: string | null;
  invoice_id: string | null;
  ot_number?: number | null;
  amount_paid?: number | null;
  payment_status?: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  closed_at: string | null;
};

function mapPaymentStatus(raw: string | null | undefined): WorkOrderPaymentStatus {
  if (raw === 'deposit_paid' || raw === 'paid' || raw === 'waived') return raw;
  return 'unpaid';
}

function mapRow(row: WorkOrderRow): WorkOrder {
  return {
    id: row.id,
    organizationId: row.organization_id,
    branchId: row.branch_id,
    subjectType: normalizeWorkOrderSubjectType(row.subject_type),
    subjectId: row.subject_id,
    customerId: row.customer_id,
    customerName: row.customer_name,
    status: row.status,
    notes: row.notes,
    appointmentId: row.appointment_id,
    saleId: row.sale_id,
    invoiceId: row.invoice_id,
    otNumber: row.ot_number != null ? Number(row.ot_number) : null,
    amountPaid: Number(row.amount_paid ?? 0),
    paymentStatus: mapPaymentStatus(row.payment_status),
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    closedAt: row.closed_at,
  };
}

function mapListItem(row: WorkOrderRow): WorkOrderListItem {
  return {
    id: row.id,
    subjectType: normalizeWorkOrderSubjectType(row.subject_type),
    subjectId: row.subject_id,
    customerId: row.customer_id,
    customerName: row.customer_name,
    status: row.status,
    notes: row.notes,
    appointmentId: row.appointment_id,
    saleId: row.sale_id,
    invoiceId: row.invoice_id,
    otNumber: row.ot_number != null ? Number(row.ot_number) : null,
    amountPaid: Number(row.amount_paid ?? 0),
    paymentStatus: mapPaymentStatus(row.payment_status),
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    closedAt: row.closed_at,
  };
}

function throwWorkOrderWriteFailure(op: string, error: unknown, itemId?: string): never {
  const err = error instanceof Error ? error : new Error(String(error));
  captureOpsFailure({
    module: 'work-orders',
    op,
    error: err,
    ingestError: err.message,
    itemId,
    extra: itemId ? { workOrderId: itemId } : undefined,
  });
  throw err;
}

export async function listBranchWorkOrders(
  branchId: string,
  status?: WorkOrderStatus | null,
  search?: string | null,
  assignedEmployeeId?: string | null,
): Promise<WorkOrderListItem[]> {
  const { data, error } = await supabase.rpc('list_branch_work_orders', {
    p_branch_id: branchId,
    p_status: status ?? null,
    p_search: search?.trim() || null,
    p_assigned_employee_id: assignedEmployeeId ?? null,
  });
  if (error) throw new Error(error.message);
  return ((data ?? []) as WorkOrderRow[]).map(mapListItem);
}

export async function createWorkOrder(
  organizationId: string,
  draft: WorkOrderDraft,
): Promise<string> {
  const { data, error } = await supabase.rpc('create_work_order', {
    p_organization_id: organizationId,
    p_branch_id: draft.branchId,
    p_subject_type: normalizeWorkOrderSubjectType(draft.subjectType),
    p_subject_id: draft.subjectId,
    p_customer_id: draft.customerId,
    p_customer_name: draft.customerName,
    p_notes: draft.notes || null,
    p_appointment_id: draft.appointmentId,
  });
  if (error) throwWorkOrderWriteFailure('work_orders.create', new Error(error.message));
  const id = data as string | null;
  if (!id) throwWorkOrderWriteFailure('work_orders.create', new Error('No se pudo crear la orden de trabajo.'));
  return id;
}

export async function updateWorkOrderStatus(
  workOrderId: string,
  status: WorkOrderStatus,
): Promise<void> {
  const { error } = await supabase.rpc('update_work_order_status', {
    p_work_order_id: workOrderId,
    p_status: status,
  });
  if (error) throwWorkOrderWriteFailure('work_orders.status', new Error(error.message), workOrderId);
}

export async function updateWorkOrderNotes(
  workOrderId: string,
  notes: string,
): Promise<void> {
  const { error } = await supabase.rpc('update_work_order_notes', {
    p_work_order_id: workOrderId,
    p_notes: notes,
  });
  if (error) throw new Error(error.message);
}

export async function getWorkOrderDetail(workOrderId: string): Promise<WorkOrderDetail | null> {
  const { data, error } = await supabase.rpc('get_work_order', {
    p_work_order_id: workOrderId,
  });
  if (error) throw new Error(error.message);
  if (!data) return null;

  const payload = data as {
    work_order: WorkOrderRow;
    tasks: Array<Record<string, unknown>>;
    parts: Array<Record<string, unknown>>;
    photos: Array<Record<string, unknown>>;
    checklists: Array<Record<string, unknown>>;
  };

  return {
    workOrder: mapRow(payload.work_order),
    tasks: payload.tasks.map(mapTask),
    parts: payload.parts.map(mapPart),
    photos: payload.photos.map(mapPhoto),
    checklists: payload.checklists.map(mapChecklist),
  };
}

function mapTask(row: Record<string, unknown>): WorkOrderTask {
  return {
    id: String(row.id),
    workOrderId: String(row.work_order_id),
    productId: String(row.product_id),
    productName: String(row.product_name ?? ''),
    quantity: Number(row.quantity ?? 1),
    unitPrice: Number(row.unit_price ?? 0),
    notes: (row.notes as string | null) ?? null,
    sortOrder: Number(row.sort_order ?? 0),
    assignedToEmployeeId: (row.assigned_to_employee_id as string | null) ?? null,
    commissionPercent: row.commission_percent != null ? Number(row.commission_percent) : null,
    completedAt: (row.completed_at as string | null) ?? null,
    createdAt: String(row.created_at),
  };
}

function mapPart(row: Record<string, unknown>): WorkOrderPart {
  return {
    id: String(row.id),
    workOrderId: String(row.work_order_id),
    productId: String(row.product_id),
    productName: String(row.product_name ?? ''),
    quantity: Number(row.quantity ?? 1),
    unitPrice: Number(row.unit_price ?? 0),
    notes: (row.notes as string | null) ?? null,
    stockDeducted: Boolean(row.stock_deducted ?? false),
    sortOrder: Number(row.sort_order ?? 0),
    createdAt: String(row.created_at),
  };
}

function mapPhoto(row: Record<string, unknown>): WorkOrderPhoto {
  return {
    id: String(row.id),
    workOrderId: String(row.work_order_id),
    storagePath: String(row.storage_path),
    caption: (row.caption as string | null) ?? null,
    sortOrder: Number(row.sort_order ?? 0),
    createdAt: String(row.created_at),
  };
}

function mapChecklist(row: Record<string, unknown>): WorkOrderChecklist {
  const items = Array.isArray(row.items) ? (row.items as WorkOrderChecklistItem[]) : [];
  return {
    id: String(row.id),
    workOrderId: String(row.work_order_id),
    templateName: String(row.template_name ?? ''),
    items,
    completedBy: (row.completed_by as string | null) ?? null,
    completedAt: (row.completed_at as string | null) ?? null,
    createdAt: String(row.created_at),
  };
}

export async function addWorkOrderTask(input: {
  workOrderId: string;
  productId: string;
  productName: string;
  quantity?: number;
  unitPrice?: number;
  notes?: string | null;
  assignedToEmployeeId?: string | null;
  commissionPercent?: number | null;
  sortOrder?: number;
}): Promise<string> {
  const { data, error } = await supabase.rpc('add_work_order_task', {
    p_work_order_id: input.workOrderId,
    p_product_id: input.productId,
    p_product_name: input.productName,
    p_quantity: input.quantity ?? 1,
    p_unit_price: input.unitPrice ?? 0,
    p_notes: input.notes ?? null,
    p_assigned_to_employee_id: input.assignedToEmployeeId ?? null,
    p_commission_percent: input.commissionPercent ?? null,
    p_sort_order: input.sortOrder ?? 0,
  });
  if (error) throw new Error(error.message);
  return data as string;
}

export async function removeWorkOrderTask(taskId: string): Promise<void> {
  const { error } = await supabase.rpc('remove_work_order_task', { p_task_id: taskId });
  if (error) throw new Error(error.message);
}

export async function addWorkOrderPart(input: {
  workOrderId: string;
  productId: string;
  productName: string;
  quantity?: number;
  unitPrice?: number;
  notes?: string | null;
  sortOrder?: number;
}): Promise<string> {
  const { data, error } = await supabase.rpc('add_work_order_part', {
    p_work_order_id: input.workOrderId,
    p_product_id: input.productId,
    p_product_name: input.productName,
    p_quantity: input.quantity ?? 1,
    p_unit_price: input.unitPrice ?? 0,
    p_notes: input.notes ?? null,
    p_sort_order: input.sortOrder ?? 0,
  });
  if (error) throw new Error(error.message);
  return data as string;
}

export async function removeWorkOrderPart(partId: string): Promise<void> {
  const { error } = await supabase.rpc('remove_work_order_part', { p_part_id: partId });
  if (error) throw new Error(error.message);
}

export async function updateWorkOrderTask(input: {
  taskId: string;
  quantity?: number | null;
  unitPrice?: number | null;
  notes?: string | null;
  assignedToEmployeeId?: string | null;
  commissionPercent?: number | null;
}): Promise<void> {
  const { error } = await supabase.rpc('update_work_order_task', {
    p_task_id: input.taskId,
    p_quantity: input.quantity ?? null,
    p_unit_price: input.unitPrice ?? null,
    p_notes: input.notes ?? null,
    p_assigned_to_employee_id: input.assignedToEmployeeId ?? null,
    p_commission_percent: input.commissionPercent ?? null,
  });
  if (error) throw new Error(error.message);
}

export async function updateWorkOrderPart(input: {
  partId: string;
  quantity?: number | null;
  unitPrice?: number | null;
  notes?: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc('update_work_order_part', {
    p_part_id: input.partId,
    p_quantity: input.quantity ?? null,
    p_unit_price: input.unitPrice ?? null,
    p_notes: input.notes ?? null,
  });
  if (error) throw new Error(error.message);
}

export async function linkWorkOrderToSale(workOrderId: string, saleId: string): Promise<void> {
  const { error } = await supabase.rpc('link_work_order_to_sale', {
    p_work_order_id: workOrderId,
    p_sale_id: saleId,
  });
  if (error) throwWorkOrderWriteFailure('work_orders.link_sale', new Error(error.message), workOrderId);
}

export async function recordWorkOrderPayment(input: {
  workOrderId: string;
  saleId: string;
  amount: number;
  kind: WorkOrderPaymentKind;
}): Promise<void> {
  const { error } = await supabase.rpc('record_work_order_payment', {
    p_work_order_id: input.workOrderId,
    p_sale_id: input.saleId,
    p_amount: input.amount,
    p_kind: input.kind,
  });
  if (error) throwWorkOrderWriteFailure('work_orders.payment', new Error(error.message), input.workOrderId);
}

export async function ensureWorkOrderPublicToken(workOrderId: string): Promise<string> {
  const { data, error } = await supabase.rpc('ensure_work_order_public_token', {
    p_work_order_id: workOrderId,
  });
  if (error) throw new Error(error.message);
  const token = data as string | null;
  if (!token) throw new Error('No se pudo generar el QR de la OT.');
  return token;
}

export async function linkWorkOrderToAppointment(
  workOrderId: string,
  appointmentId: string | null,
): Promise<void> {
  const { error } = await supabase.rpc('link_work_order_to_appointment', {
    p_work_order_id: workOrderId,
    p_appointment_id: appointmentId,
  });
  if (error) throw new Error(error.message);
}

export async function findWorkOrderByAppointment(appointmentId: string): Promise<string | null> {
  const { data, error } = await supabase.rpc('find_work_order_by_appointment', {
    p_appointment_id: appointmentId,
  });
  if (error) throw new Error(error.message);
  return (data as string | null) ?? null;
}

function decodeBase64(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** Upload image to work-order-photos bucket and register row. Returns public URL. */
export async function uploadWorkOrderPhoto(input: {
  workOrderId: string;
  organizationId: string;
  base64: string;
  mimeType: string;
  caption?: string | null;
}): Promise<string> {
  const ext = input.mimeType.includes('png')
    ? 'png'
    : input.mimeType.includes('webp')
      ? 'webp'
      : 'jpg';
  const photoId = crypto.randomUUID();
  const storagePath = `${input.organizationId}/${input.workOrderId}/${photoId}.${ext}`;

  const { error: uploadError } = await supabase.storage
    .from('work-order-photos')
    .upload(storagePath, decodeBase64(input.base64), {
      contentType: input.mimeType || 'image/jpeg',
      upsert: false,
    });
  if (uploadError) throw new Error(uploadError.message);

  const { data: pub } = supabase.storage.from('work-order-photos').getPublicUrl(storagePath);
  const publicUrl = pub.publicUrl;

  const { error } = await supabase.rpc('add_work_order_photo', {
    p_work_order_id: input.workOrderId,
    p_storage_path: publicUrl,
    p_caption: input.caption ?? null,
    p_sort_order: 0,
  });
  if (error) throw new Error(error.message);
  return publicUrl;
}

export async function removeWorkOrderPhoto(photoId: string): Promise<void> {
  const { error } = await supabase.rpc('remove_work_order_photo', {
    p_photo_id: photoId,
  });
  if (error) throw new Error(error.message);
}

// ============================================================
// H-22: Stock deduction for work order parts
// ============================================================

/**
 * Marks all parts of a work order as stock_deducted=true (idempotent).
 * Use after a POS sale that already deducted stock via cart items.
 */
export async function markWorkOrderPartsDeducted(workOrderId: string): Promise<void> {
  const { error } = await supabase.rpc('mark_work_order_parts_deducted', {
    p_work_order_id: workOrderId,
  });
  if (error) throw new Error(error.message);
}

/**
 * Deducts stock for a single work order part (manual confirmation).
 * Inserts a stock_movement and updates stocks.quantity. Idempotent.
 */
export async function deductWorkOrderPartStock(partId: string): Promise<void> {
  const { error } = await supabase.rpc('deduct_work_order_part_stock', {
    p_work_order_part_id: partId,
  });
  if (error) throwWorkOrderWriteFailure('work_orders.stock_deduct', new Error(error.message), partId);
}

/**
 * Links a work order to an invoice ID (post-fiscal emission).
 */
export async function linkWorkOrderToInvoice(
  workOrderId: string,
  invoiceId: string,
): Promise<void> {
  const { error } = await supabase.rpc('link_work_order_to_invoice', {
    p_work_order_id: workOrderId,
    p_invoice_id: invoiceId,
  });
  if (error) throw new Error(error.message);
}

// ============================================================
// Checklists (H-17)
// ============================================================

export async function addWorkOrderChecklist(input: {
  workOrderId: string;
  templateName?: string;
  items?: WorkOrderChecklistItem[];
}): Promise<string> {
  const { data, error } = await supabase.rpc('add_work_order_checklist', {
    p_work_order_id: input.workOrderId,
    p_template_name: input.templateName ?? '',
    p_items: JSON.stringify(input.items ?? []),
  });
  if (error) throw new Error(error.message);
  return data as string;
}

export async function updateWorkOrderChecklistItems(
  checklistId: string,
  items: WorkOrderChecklistItem[],
): Promise<void> {
  const { error } = await supabase.rpc('update_work_order_checklist_items', {
    p_checklist_id: checklistId,
    p_items: JSON.stringify(items),
  });
  if (error) throw new Error(error.message);
}

export async function completeWorkOrderChecklist(checklistId: string): Promise<void> {
  const { error } = await supabase.rpc('complete_work_order_checklist', {
    p_checklist_id: checklistId,
  });
  if (error) throw new Error(error.message);
}

export async function removeWorkOrderChecklist(checklistId: string): Promise<void> {
  const { error } = await supabase.rpc('remove_work_order_checklist', {
    p_checklist_id: checklistId,
  });
  if (error) throw new Error(error.message);
}

export type ChecklistTemplate = {
  id: string;
  name: string;
  items: WorkOrderChecklistItem[];
  isDefault: boolean;
};

export async function listChecklistTemplates(
  businessTypeCode?: string | null,
): Promise<ChecklistTemplate[]> {
  const { data, error } = await supabase.rpc('list_checklist_templates', {
    p_business_type_code: businessTypeCode ?? null,
  });
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as Array<{ id: string; name: string; items: WorkOrderChecklistItem[]; is_default: boolean }>;
  return rows.map((r) => ({
    id: r.id,
    name: r.name,
    items: Array.isArray(r.items) ? r.items : [],
    isDefault: Boolean(r.is_default),
  }));
}
